import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import LoadingStatus from '@libs/loading-status';

import GLOBAL_ERROR_MESSAGE from '@app/constants/errorMessages';
import { RootState } from '@app/store/types';

export const fetchEmbeddedDocumentRequest = createAction<{ documentId: string; signable: boolean }>(
  'embeddedDocument/fetchEmbeddedDocumentRequest'
);

export const fetchEmbeddedDocumentSucceeded = createAction(
  'embeddedDocument/fetchEmbeddedDocumentSucceded'
);

export const fetchEmbeddedDocumentFailed = createAction<AxiosError>(
  'embeddedDocument/fetchEmbeddedDocumentFailed'
);

interface EmbeddedDocumentState {
  error: {
    title: string | null;
    content: string;
  } | null;
  loadingStatus: LoadingStatus;
  signable: boolean;
}

const initialEmbeddedDocumentState: EmbeddedDocumentState = {
  error: null,
  loadingStatus: LoadingStatus.IDLE,
  signable: false,
};

const embeddedDocumentSlice = createSlice({
  name: 'embeddedDocument',
  initialState: initialEmbeddedDocumentState,
  reducers: {
    setError(state, action: PayloadAction<{ title: string | null; content: string } | null>) {
      state.error = action.payload;
    },
    setSignable(state, action: PayloadAction<boolean>) {
      state.signable = action.payload;
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(fetchEmbeddedDocumentRequest, (state) => {
        state.loadingStatus = LoadingStatus.PENDING;
      })
      .addCase(fetchEmbeddedDocumentSucceeded, (state) => {
        state.loadingStatus = LoadingStatus.SUCCEEDED;
      })
      .addCase(fetchEmbeddedDocumentFailed, (state, action) => {
        state.loadingStatus = LoadingStatus.FAILED;
        state.error = {
          title: '서명 요청 불러오기 실패',
          content: GLOBAL_ERROR_MESSAGE[action.payload.message] || action.payload.message,
        };
      }),
});

const getEmbeddedDocument = (state: RootState) => state.embeddedDocument;

export const getError = (state: RootState) => getEmbeddedDocument(state).error;

export const getIsSignable = (state: RootState) => getEmbeddedDocument(state).signable;

export const getLoadingStatus = (state: RootState) => getEmbeddedDocument(state).loadingStatus;

export const { setError, setSignable } = embeddedDocumentSlice.actions;

export const { reducer } = embeddedDocumentSlice;
